import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import quantService from '../../../services/quantService';

const fmt = (v, n = 4) => (v == null || isNaN(v)) ? '—' : Number(v).toFixed(n);
const pct = (v, n = 1) => (v == null || isNaN(v)) ? '—' : `${(Number(v) * 100).toFixed(n)}%`;
const numColor = (v) =>
  v == null || isNaN(v) ? 'text-gray-400'
  : v > 0 ? 'text-emerald-600 dark:text-emerald-400'
  : v < 0 ? 'text-red-500 dark:text-red-400' : 'text-gray-400';

const MonitoringPage = () => {
  const navigate = useNavigate();
  const [deployed, setDeployed] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [icSeries, setIcSeries] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    quantService.listDiscovery()
      .then(({ data }) => {
        const live = (data || []).filter(j => j.alphalens_passed === true);
        setDeployed(live);
        if (live.length && !selectedJob) setSelectedJob(live[0].job_id);
      })
      .catch(e => setError(e?.response?.data?.detail || e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedJob) return;
    quantService.getICTimeseries(selectedJob)
      .then(({ data }) => setIcSeries(data || []))
      .catch(e => setError(e?.response?.data?.detail || e.message));
  }, [selectedJob]);

  if (error) return <div className="p-6 text-sm text-red-500">{error}</div>;

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-4">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300 mb-3">
          Deployed Signals
        </h2>
        {deployed.length === 0 ? (
          <p className="text-xs text-gray-400">
            No Alphalens-passed discoveries yet. Run a discovery and validate it before live monitoring becomes available.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {deployed.map(d => (
              <button
                key={d.job_id}
                onClick={() => setSelectedJob(d.job_id)}
                className={`px-3 py-1.5 text-xs font-mono rounded ${
                  selectedJob === d.job_id
                    ? 'bg-brand-gold/15 text-brand-gold border border-brand-gold/30'
                    : 'bg-gray-50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-400 hover:bg-gray-100'
                }`}
              >
                {d.job_id.slice(0, 8)}… {d.universe_name}
              </button>
            ))}
          </div>
        )}
      </div>

      {selectedJob && (
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
              Live IC vs Backtest IC
            </h2>
            <button onClick={() => navigate(`/trading/quant/jobs/${selectedJob}`)}
                    className="text-xs uppercase tracking-wider text-gray-500 hover:text-brand-gold">
              View discovery →
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/50 text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="text-left px-4 py-2">Date</th>
                  <th className="text-right px-4 py-2">Live IC (63d)</th>
                  <th className="text-right px-4 py-2">Backtest IC</th>
                  <th className="text-right px-4 py-2">Degradation</th>
                  <th className="text-center px-4 py-2">Alert</th>
                </tr>
              </thead>
              <tbody>
                {icSeries.length === 0 && (
                  <tr><td colSpan={5} className="text-center py-6 text-gray-400 text-sm">
                    No monitor records yet. The daily_ic_monitor beat task runs at 07:00 UTC weekdays.
                  </td></tr>
                )}
                {icSeries.map(r => (
                  <tr key={r.id} className="border-t border-gray-100 dark:border-gray-800/50">
                    <td className="px-4 py-1.5 text-xs font-mono">{r.date}</td>
                    <td className={`px-4 py-1.5 text-right text-xs font-mono ${numColor(r.live_ic_63d)}`}>{fmt(r.live_ic_63d, 4)}</td>
                    <td className={`px-4 py-1.5 text-right text-xs font-mono ${numColor(r.backtest_ic)}`}>{fmt(r.backtest_ic, 4)}</td>
                    <td className={`px-4 py-1.5 text-right text-xs font-mono ${
                      r.degradation < -0.5 ? 'text-red-500' : r.degradation < 0 ? 'text-amber-500' : 'text-emerald-500'
                    }`}>
                      {pct(r.degradation, 1)}
                    </td>
                    <td className="px-4 py-1.5 text-center text-xs">
                      {r.alert_sent ? <span className="text-red-500">⚠</span> : <span className="text-emerald-500">✓</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonitoringPage;
